/**
 * WebSocket event names and namespaces (spec Part 12).
 * Amendment-001 removed Metrc budget admin events — see REMOVED_WS_EVENT_NAMES.
 */
/** Namespaces: `/ws/orders/:orderId`, `/ws/dispensary/:id`, `/ws/transporter/:id`, `/ws/admin` */
export function wsOrdersNamespace(orderId) {
    return `/ws/orders/${orderId}`;
}
export function wsDispensaryNamespace(dispensaryId) {
    return `/ws/dispensary/${dispensaryId}`;
}
export function wsTransporterNamespace(transporterId) {
    return `/ws/transporter/${transporterId}`;
}
export const WS_ADMIN_NAMESPACE = "/ws/admin";
/**
 * Canonical event names — use these literals only (no ad-hoc strings) when emitting/handling.
 * @see agent-task-document.md Part 12
 */
export const WS_EVENT_NAMES = [
    // Customer + shared operational
    "order.confirmed",
    "order.driver_assigned",
    "driver.location",
    "run.departed",
    "stop.id_verified",
    "stop.completed",
    "stop.failed",
    "order.completed",
    "order.cancelled",
    /**
     * Customer-safe ETA window for one order — coordinates-free, position-free per
     * `docs/driver-eta.md`. Contract added here so consumers can subscribe; the server-side compute
     * + emit lands in the ETA-feature PR.
     */
    "order.eta",
    // Dispensary queue / ops
    "order.received",
    "order.auto_cancelled",
    "run.driver_confirmed",
    "run.arrived_at_dispensary",
    "run.package_scan_failed",
    "run.completed",
    "run.void_requested",
    "batch.ready_for_approval",
    "driver.eta_update",
    "alert.driver_rejection",
    "alert.no_drivers_available",
    /**
     * Internal-only run-level ETA (stops-ahead + per-order ETA windows). Routed to dispensary +
     * transporter namespaces only; must NEVER reach customer namespaces (richer payload than
     * `order.eta`). Contract added here; emit lands in the ETA-feature PR.
     */
    "run.eta",
    // Transporter fleet / driver
    "driver.checked_in",
    "driver.checked_out",
    "run.assigned",
    "alert.acceptance_expiring",
    "alert.scan_failure",
    "alert.supervisor_required",
    "settlement.approved",
    // Admin
    "alert.payment_failed",
    "alert.credential_expiry",
    "alert.onboarding_blocked",
    "alert.manifest_webhook_no_match",
    "platform.revenue_update",
    /** Admin ops — Straddle retry handoff (**DEV-049** / **DEV-051**). */
    "payment_transfer.admin_retry_started",
];
/**
 * Import these at Socket.io **emit** call sites (**DEV-051**) — avoids raw string literals
 * that drift from **`WS_EVENT_NAMES`**.
 */
export const WS_EVENT = {
    ORDER_RECEIVED: "order.received",
    DRIVER_LOCATION: "driver.location",
    DRIVER_ETA_UPDATE: "driver.eta_update",
    RUN_ARRIVED_AT_DISPENSARY: "run.arrived_at_dispensary",
    RUN_DEPARTED: "run.departed",
    RUN_COMPLETED: "run.completed",
    ORDER_ETA: "order.eta",
    RUN_ETA: "run.eta",
    PAYMENT_TRANSFER_ADMIN_RETRY_STARTED: "payment_transfer.admin_retry_started",
    BATCH_READY_FOR_APPROVAL: "batch.ready_for_approval",
    ALERT_PAYMENT_FAILED: "alert.payment_failed",
    ALERT_CREDENTIAL_EXPIRY: "alert.credential_expiry",
};
/**
 * Removed by amendment-001 — must never appear in emitters or WS_EVENT_NAMES.
 * @see docs/dev-stories.md DEV-051
 */
export const REMOVED_WS_EVENT_NAMES = [
    "alert.metrc_budget_warning",
    "alert.metrc_budget_critical",
];
/**
 * **Compliance gate** — events that must **never** reach the customer-facing **`/ws/orders/:orderId`**
 * namespace. Cannabis-delivery regulator concern: anyone observing the customer can correlate a
 * driver-with-product to a public street position in real time, so coordinate-bearing events
 * (`driver.location`) cannot fan out to customers regardless of payload shape.
 *
 * Enforcement is two-layered in `services/api/src/ws`:
 *   1. `broadcastWsEventMessage` → `collectOrderNamespaceTargets` returns `[]` for denylisted events.
 *   2. `emitToOrderNamespace` helper asserts the denylist before calling `io.of(...).emit(...)` —
 *      catches re-introduction via any future code path that bypasses `broadcastWsEventMessage`.
 *
 * @see docs/live-driver-map-relocation.md
 */
export const CUSTOMER_NAMESPACE_DENYLIST = new Set([
    "driver.location",
    // `run.eta` carries fleet-level stops-ahead + per-order ETA windows — internal-only by design.
    // The customer-safe ETA window goes through `order.eta` (single-order, no run context).
    "run.eta",
    // The attempt-cap alert carries dispatch history (how many drivers refused the order).
    // Store and transporter only; the customer sees status copy, never dispatch attempts.
    "alert.no_drivers_available",
]);
//# sourceMappingURL=ws-events.js.map